import {
  FormController,
  FormFileUpload,
  FormInput,
  FormTextArea,
} from '@/components/form';
import {
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  ATTACHMENT_MAX_FILE_SIZE_LABEL,
} from '@/constants/uploadLimits';
import { CoreApi, RfiApi } from '@/state/api';
import { CoreType } from '@/types';
import { getFileNameWithoutExtension } from '@/utils/utilFunctions.ts';
import {
  Badge,
  Button,
  Center,
  Checkbox,
  FileUploadFileMimeType,
  Flex,
  Separator,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import React, { Suspense, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form';
import { LuDot } from 'react-icons/lu';
import z from 'zod';
import ManageCard from '../ManageCard';
import ViewReturnAttachmentsCard from '../ViewReturnAttachmentsCard/index.tsx';
import { toDateInputValue } from '@/utils/dateFields';
import { getRfiLogExportDateDefaults } from './exportDates';

const ViewAttachmentsCard = React.lazy(() => import('../ViewAttachmentsCard'));

type RfiLogDetailsProps = {
  rfi_log_id: number | null;
  onClose: () => void;
};

const RfiLogDetails = ({ rfi_log_id, onClose }: RfiLogDetailsProps) => {
  const [isExporting, setIsExporting] = useState(false);

  // Post pre signed urls
  const [postAttachmentsPresignedUrlsMutation] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  // Post Rfi Log Attachments
  const [postRfiLogAttachmentsMutation] =
    RfiApi.Api.usePostRfiLogAttachmentsMutation();

  //export rfi log
  const [postRfiLogExportPdfMutation] =
    RfiApi.Api.usePostRfiLogExportPdfMutation();

  //delete rfi
  const [deleteRfiLogMutation] = RfiApi.Api.useDeleteRfiLogMutation();

  // Get Rfi Log
  const canFetch = typeof rfi_log_id === 'number' && rfi_log_id > 0;

  const [postRfiReturnAttachmentMutation] =
    RfiApi.Api.usePostRfiReturnAttachmentMutation();

  const [deleteRfiReturnAttachmentMutation] =
    RfiApi.Api.useDeleteRfiReturnAttachmentMutation();

  const { data: returnAttachmentsData } =
    RfiApi.Api.useGetRfiReturnAttachmentsQuery(
      { params: { rfi_log_id: rfi_log_id as number } },
      { skip: !canFetch },
    );
  const returnAttachments = returnAttachmentsData ?? [];
  const { data } = RfiApi.Api.useGetRfiLogQuery(
    { params: { rfi_log_id: rfi_log_id as number } },
    { skip: !canFetch },
  );

  const rfiLog = data?.data;

  const openDeleteConfirm = () => {
    if (!rfi_log_id) return;

    OverlayDialog.open('confirmDeleteRfiLog', {
      overlayId: 'confirmDeleteRfiLog',
      title: 'Delete RFI?',
      content: (
        <Flex flexDirection="column" gap={4} width="full">
          <Text>
            This will permanently delete this RFI and all its attachments. This
            cannot be undone.
          </Text>

          <Flex justifyContent="flex-end" gap={2}>
            <Button
              variant="outline"
              onClick={() => OverlayDialog.close('confirmDeleteRfiLog')}
            >
              Cancel
            </Button>

            <Button
              colorPalette="red"
              onClick={async () => {
                const id = rfi_log_id; // capture
                if (!id) return;

                //  Unmount immediately so the detail query is gone
                onClose();
                OverlayDialog.close('confirmDeleteRfiLog');

                try {
                  await deleteRfiLogMutation({
                    params: { rfi_log_id: id },
                  }).unwrap();

                  toasterApi({
                    type: 'success',
                    status: 204,
                    code: 'Deleted',
                    message: 'RFI deleted successfully',
                  });
                } catch (e: any) {
                  toasterApi({
                    type: 'error',
                    status: 500,
                    code: 'Error',
                    message:
                      e?.data?.detail || e?.message || 'Failed to delete RFI',
                  });
                }
              }}
            >
              Delete
            </Button>
          </Flex>
        </Flex>
      ),
    });
  };

  const handleExportPdf = () => {
    if (!rfi_log_id) return;

    const exportDateDefaults = getRfiLogExportDateDefaults(rfiLog);

    OverlayDialog.open('exportRfiLogPdf', {
      overlayId: 'exportRfiLogPdf',
      title: 'Export RFI PDF',
      content: (
        <Flex width="full">
          <FormController
            size="lg"
            formSchema={rfiExportFormSchema}
            defaultValues={{
              to_name: rfiLog?.export_to_name || '',
              spec_ref: rfiLog?.export_spec_ref || '',
              ...exportDateDefaults,
              description: rfiLog?.question || '',

              contract_drawing_ref: rfiLog?.export_contract_drawing_ref || '',
              shop_drawing_ref: rfiLog?.export_shop_drawing_ref || '',
              distribution_list: rfiLog?.export_distribution_list || '',

              information_not_on_contract:
                !!rfiLog?.export_information_not_on_contract,
              interpretation_of_contract:
                !!rfiLog?.export_interpretation_of_contract,
              conflict_in_contract: !!rfiLog?.export_conflict_in_contract,
              coordination_problem: !!rfiLog?.export_coordination_problem,
            }}
            ButtonComponent={(props: any) => (
              <Button
                {...props}
                colorPalette="blue"
                alignSelf="flex-end"
                mt="4"
                isDisabled={isExporting || props?.isDisabled}
              >
                {isExporting && <Spinner size="xs" mr={2} />}
                {isExporting ? 'Exporting…' : 'Export PDF'}
              </Button>
            )}
            onFormSubmit={async (formData) => {
              toasterApi({
                type: 'success',
                status: 200,
                code: 'Debug',
                message: 'Submitting export…',
              });

              setIsExporting(true);
              try {
                const payload = {
                  to_name: formData.to_name,
                  spec_ref: formData.spec_ref,
                  date: formData.date,
                  date_required: formData.date_required,
                  description: formData.description,
                  category: {
                    information_not_on_contract:
                      formData.information_not_on_contract ? 1 : 0,
                    interpretation_of_contract:
                      formData.interpretation_of_contract ? 1 : 0,
                    conflict_in_contract: formData.conflict_in_contract ? 1 : 0,
                    coordination_problem: formData.coordination_problem ? 1 : 0,
                  },
                  contract_drawing_ref: formData.contract_drawing_ref ?? '',
                  shop_drawing_ref: formData.shop_drawing_ref ?? '',
                  distribution_list: formData.distribution_list
                    ? [formData.distribution_list]
                    : [],
                };

                const blob = await postRfiLogExportPdfMutation({
                  params: { rfi_log_id },
                  body: payload,
                }).unwrap();

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${rfiLog?.rfi_number || `rfi_${rfi_log_id}`}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);

                toasterApi({
                  type: 'success',
                  status: 200,
                  code: 'Success',
                  message: 'PDF exported successfully',
                });

                OverlayDialog.close('exportRfiLogPdf');
              } catch (e: any) {
                toasterApi({
                  type: 'error',
                  status: 500,
                  code: 'Error',
                  message:
                    e?.data?.detail || e?.message || 'Failed to export PDF',
                });
              } finally {
                setIsExporting(false);
              }
            }}
          >
            {({
              register,
              control,
              errors,
            }: {
              register: UseFormRegister<RfiExportFormType>;
              control: Control<RfiExportFormType>;
              errors: FieldErrors<RfiExportFormType>;
            }) => (
              <Flex flexDirection="column" gapY={6} width="full">
                <VStack align="stretch" gap={3}>
                  <Text fontWeight="bold">Category</Text>

                  <Controller
                    name="information_not_on_contract"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Checkbox.Root
                        checked={!!field.value}
                        onCheckedChange={(e: any) => {
                          const next = typeof e === 'boolean' ? e : e?.checked;
                          field.onChange(next === true);
                        }}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <Checkbox.Label>
                          Information not on contract
                        </Checkbox.Label>
                      </Checkbox.Root>
                    )}
                  />

                  <Controller
                    name="interpretation_of_contract"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Checkbox.Root
                        checked={!!field.value}
                        onCheckedChange={(e: any) => {
                          const next = typeof e === 'boolean' ? e : e?.checked;
                          field.onChange(next === true);
                        }}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <Checkbox.Label>
                          Interpretation of contract
                        </Checkbox.Label>
                      </Checkbox.Root>
                    )}
                  />

                  <Controller
                    name="conflict_in_contract"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Checkbox.Root
                        checked={!!field.value}
                        onCheckedChange={(e: any) => {
                          const next = typeof e === 'boolean' ? e : e?.checked;
                          field.onChange(next === true);
                        }}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <Checkbox.Label>Conflict in contract</Checkbox.Label>
                      </Checkbox.Root>
                    )}
                  />

                  <Controller
                    name="coordination_problem"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Checkbox.Root
                        checked={!!field.value}
                        onCheckedChange={(e: any) => {
                          const next = typeof e === 'boolean' ? e : e?.checked;
                          field.onChange(next === true);
                        }}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <Checkbox.Label>Coordination problem</Checkbox.Label>
                      </Checkbox.Root>
                    )}
                  />
                </VStack>

                <FormInput
                  label="Contract drawing ref"
                  id="contract_drawing_ref"
                  register={register}
                  control={control}
                  errors={errors}
                  showLabelAtTop
                />

                <FormInput
                  label="Shop drawing ref"
                  id="shop_drawing_ref"
                  register={register}
                  control={control}
                  errors={errors}
                  showLabelAtTop
                />

                <FormTextArea
                  label="Distribution list"
                  id="distribution_list"
                  register={register}
                  control={control}
                  errors={errors}
                  showLabelAtTop
                  minH="5lh"
                  maxH="5lh"
                />

                <FormInput
                  label="To"
                  id="to_name"
                  register={register}
                  control={control}
                  errors={errors}
                  showLabelAtTop
                />

                <FormInput
                  label="Specification Ref"
                  id="spec_ref"
                  register={register}
                  control={control}
                  errors={errors}
                  showLabelAtTop
                />

                <Flex gapX={4}>
                  <FormInput
                    label="Date"
                    id="date"
                    register={register}
                    control={control}
                    errors={errors}
                    showLabelAtTop
                    type="date"
                  />

                  <FormInput
                    label="Date Info Req'd"
                    id="date_required"
                    register={register}
                    control={control}
                    errors={errors}
                    showLabelAtTop
                    type="date"
                  />
                </Flex>

                <FormTextArea
                  label="Description"
                  id="description"
                  register={register}
                  control={control}
                  errors={errors}
                  showLabelAtTop
                  minH="5lh"
                  maxH="5lh"
                />
              </Flex>
            )}
          </FormController>
        </Flex>
      ),
    });
  };

  const handleSubmitReturnAttachments = () => {
    OverlayDialog.open('rfiReturnAttachments', {
      overlayId: 'rfiReturnAttachments',
      title: 'Add Returned Documents',
      content: (
        <Flex>
          <FormController
            size="lg"
            formSchema={fileUploadFormSchema}
            defaultValues={{
              attachmentFiles: [],
            }}
            onFormSubmit={async (formData) => {
              if (!formData?.attachmentFiles?.length || !rfi_log_id) {
                OverlayDialog.close('rfiReturnAttachments');
                return;
              }

              try {
                const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'] =
                  formData?.attachmentFiles?.map((file) => ({
                    module: 'rfi_submittal_return',
                    filename: getFileNameWithoutExtension(file.name),
                    content_type: file.type,
                    size: file.size,
                  }));

                const getPresignedUrls =
                  await postAttachmentsPresignedUrlsMutation({
                    body: getPresignedUrlsPayload,
                  }).unwrap();

                const uploadResults = await Promise.allSettled(
                  getPresignedUrls?.map(async (item) => {
                    const file = formData?.attachmentFiles?.find(
                      (f) =>
                        getFileNameWithoutExtension(f.name) === item.filename,
                    );
                    if (!file)
                      throw new Error(`File not found: ${item.filename}`);

                    const res = await fetch(item.upload_url, {
                      method: 'PUT',
                      headers: { 'Content-Type': file.type },
                      body: file,
                    });

                    if (!res.ok)
                      throw new Error(`Upload failed for ${item.filename}`);

                    return {
                      filename: item.filename,
                      file: item.file_url,
                    };
                  }),
                );

                const successfullyUploadedFiles = uploadResults
                  .filter((r) => r.status === 'fulfilled')
                  .map((r) => (r as PromiseFulfilledResult<any>).value);

                for (const file of successfullyUploadedFiles) {
                  await postRfiReturnAttachmentMutation({
                    body: file,
                    params: { rfi_log_id },
                  }).unwrap();
                }

                toasterApi({
                  type: 'success',
                  status: 200,
                  code: 'Success',
                  message: 'Returned documents added successfully',
                });
              } catch {
                toasterApi({
                  type: 'error',
                  status: 500,
                  code: 'Error',
                  message: 'Failed to add one or more returned documents',
                });
              } finally {
                OverlayDialog.close('rfiReturnAttachments');
              }
            }}
          >
            {({
              register,
              control,
              errors,
            }: {
              register: UseFormRegister<FileUploadFormType>;
              control: Control<FileUploadFormType>;
              errors: FieldErrors<FileUploadFormType>;
            }) => {
              return (
                <Flex flexDirection="column" gapY={8}>
                  <Flex flexDirection="column" gapY={4}>
                    <Text fontSize="md" fontWeight="bold">
                      Upload Returned Documents
                    </Text>
                    <FormFileUpload
                      control={control}
                      register={register}
                      errors={errors}
                      id="attachmentFiles"
                      label=""
                      maxFiles={10}
                      maxFileSize={ATTACHMENT_MAX_FILE_SIZE_BYTES}
                      accept={
                        [
                          'application/pdf',
                          'application/msword',
                          'image/*',
                        ] as FileUploadFileMimeType[]
                      }
                    />
                  </Flex>
                </Flex>
              );
            }}
          </FormController>
        </Flex>
      ),
    });
  };

  const handleSubmitAttachments = () => {
    OverlayDialog.open('rfiLogAttachments', {
      overlayId: 'rfiLogAttachments',
      title: 'Add Rfi Log Attachments',
      content: (
        <Flex>
          <FormController
            size="lg"
            formSchema={fileUploadFormSchema}
            defaultValues={{
              attachmentFiles: [],
            }}
            onFormSubmit={async (formData) => {
              if (!formData?.attachmentFiles?.length || !rfi_log_id) {
                OverlayDialog.close('rfiLogAttachments');
                return;
              }

              try {
                const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'] =
                  formData?.attachmentFiles?.map((file) => ({
                    module: 'rfi_log',
                    filename: getFileNameWithoutExtension(file.name),
                    content_type: file.type,
                    size: file.size,
                  }));

                const getPresignedUrls =
                  await postAttachmentsPresignedUrlsMutation({
                    body: getPresignedUrlsPayload,
                  }).unwrap();

                const uploadResults = await Promise.allSettled(
                  getPresignedUrls?.map(async (item) => {
                    const file = formData?.attachmentFiles?.find(
                      (f) =>
                        getFileNameWithoutExtension(f.name) === item.filename,
                    );
                    if (!file)
                      throw new Error(`File not found: ${item.filename}`);

                    const res = await fetch(item.upload_url, {
                      method: 'PUT',
                      headers: { 'Content-Type': file.type },
                      body: file,
                    });

                    if (!res.ok)
                      throw new Error(`Upload failed for ${item.filename}`);

                    return {
                      filename: item.filename,
                      file: item.file_url,
                    };
                  }),
                );

                const successfullyUploadedFiles = uploadResults
                  .filter((r) => r.status === 'fulfilled')
                  .map((r) => r.value);

                if (successfullyUploadedFiles?.length) {
                  await postRfiLogAttachmentsMutation({
                    body: successfullyUploadedFiles,
                    params: { rfi_log_id },
                  }).unwrap();
                }

                toasterApi({
                  type: 'success',
                  status: 200,
                  code: 'Success',
                  message: 'Attachments added successfully',
                });
              } catch {
                toasterApi({
                  type: 'error',
                  status: 500,
                  code: 'Error',
                  message: 'Failed to add one or more attachments',
                });
              } finally {
                OverlayDialog.close('rfiLogAttachments');
              }
            }}
          >
            {({
              register,
              control,
              errors,
            }: {
              register: UseFormRegister<FileUploadFormType>;
              control: Control<FileUploadFormType>;
              errors: FieldErrors<FileUploadFormType>;
            }) => {
              return (
                <Flex flexDirection="column" gapY={8}>
                  <Flex flexDirection="column" gapY={4}>
                    <Text fontSize="md" fontWeight="bold">
                      Upload Attachment Files
                    </Text>
                    <FormFileUpload
                      control={control}
                      register={register}
                      errors={errors}
                      id="attachmentFiles"
                      label=""
                      maxFiles={10}
                      maxFileSize={ATTACHMENT_MAX_FILE_SIZE_BYTES}
                      accept={
                        [
                          'application/pdf',
                          'application/msword',
                          'image/*',
                        ] as FileUploadFileMimeType[]
                      }
                    />
                  </Flex>
                </Flex>
              );
            }}
          </FormController>
        </Flex>
      ),
    });
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Flex justifyContent="flex-end">
        <Flex flexDirection="column" gapY={2}>
          <Flex gapX={2} justifyContent="flex-end">
            <Button
              size="sm"
              variant="outline"
              color="fg"
              borderColor="fg"
              onClick={onClose}
            >
              Back
            </Button>

            <Button
              size="sm"
              onClick={() => handleExportPdf()}
              disabled={isExporting}
            >
              {isExporting && <Spinner size="xs" mr={2} />}
              {isExporting ? 'Exporting…' : 'Export PDF'}
            </Button>

            <Button
              size="sm"
              variant="outline"
              colorPalette="blue"
              onClick={() => {
                OverlayDialog.open('editRfiLog', {
                  overlayId: 'editRfiLog',
                  title: 'Edit RFI',
                  content: (
                    <Flex>
                      <ManageCard
                        overlayId="editRfiLog"
                        defaultRfiData={rfiLog?.id ? rfiLog : null}
                        isNew={false}
                      />
                    </Flex>
                  ),
                });
              }}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorPalette="red"
              onClick={openDeleteConfirm}
            >
              Delete
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Flex flexDirection="column" gapY={5}>
        <Flex gapX={2} alignItems="center">
          <Text fontSize="large">
            <strong>Rfi Number:&nbsp;</strong>
            {rfiLog?.rfi_number}
          </Text>
          <Badge colorPalette="blue">
            <Text textTransform="capitalize">{rfiLog?.status}</Text>
          </Badge>
        </Flex>

        <Text fontSize="large">
          <strong>Subject:&nbsp;</strong>
          {rfiLog?.subject}
        </Text>

        <Separator />

        <Flex>
          <Flex flex={1} flexDirection="column" gapY={2}>
            <Flex gapX={2}>
              <Text fontWeight="bold" width="100px">
                Zone:
              </Text>
              <Text textTransform="capitalize">
                {rfiLog?.zone_level || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="100px">
                Spec:
              </Text>
              <Text textTransform="capitalize">
                {rfiLog?.spec_section || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="100px">
                Opened:
              </Text>
              <Flex alignItems="center" textTransform="capitalize" as="span">
                <Text>{toDateInputValue(rfiLog?.open_date) || 'N/A'}</Text>
                <LuDot />
                <Text>{dayjs().diff(dayjs(rfiLog?.open_date), 'day')}d</Text>
              </Flex>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="100px">
                Answered:
              </Text>
              <Text>{toDateInputValue(rfiLog?.answered_date) || 'N/A'}</Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="100px">
                Closed:
              </Text>
              <Text>{toDateInputValue(rfiLog?.closed_date) || 'N/A'}</Text>
            </Flex>
          </Flex>

          <Flex flex={1} flexDirection="column" gapY={2}>
            <Flex gapX={2}>
              <Text fontWeight="bold" width="150px">
                Ball-in-court:
              </Text>
              <Text textTransform="capitalize">
                {rfiLog?.ball_in_court || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="150px">
                Impact:
              </Text>
              <Badge colorPalette="green">
                <Text textTransform="capitalize">{rfiLog?.impact}</Text>
              </Badge>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="150px">
                Refs:
              </Text>
              <Text textTransform="capitalize">{rfiLog?.refs || 'N/A'}</Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="150px">
                Due:
              </Text>
              <Flex alignItems="center" textTransform="capitalize" as="span">
                <Text>{toDateInputValue(rfiLog?.due_date) || 'N/A'}</Text>
                <LuDot />
                <Text>{dayjs().diff(dayjs(rfiLog?.due_date), 'day')}d</Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        <Separator />

        <Flex flexDirection="column" gapY={2}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontWeight="bold" fontSize="lg">
              Attachments
            </Text>
            <Button onClick={handleSubmitAttachments}>Add Attachments</Button>
          </Flex>
          <Flex width="full">
            <Suspense fallback={<Center>Loading attachments...</Center>}>
              <ViewAttachmentsCard attachments={rfiLog?.attachments ?? []} />
            </Suspense>
          </Flex>
        </Flex>

        <Separator />

        <Flex flexDirection="column" gapY={2}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontWeight="bold" fontSize="lg">
              Returned Documents
            </Text>
            <Button onClick={handleSubmitReturnAttachments}>
              Add Returned Documents
            </Button>
          </Flex>
          <Flex width="full">
            <Suspense fallback={<Center>Loading returned documents...</Center>}>
              <ViewReturnAttachmentsCard
                attachments={returnAttachments}
                onDelete={(returnId) =>
                  deleteRfiReturnAttachmentMutation({
                    params: {
                      return_id: returnId,
                      rfi_log_id: rfi_log_id as number,
                    },
                  }).unwrap()
                }
              />
            </Suspense>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default RfiLogDetails;

const fileUploadFormSchema = z.object({
  attachmentFiles: z
    .array(z.file())
    .refine(
      (files) => files.every((f) => f.size <= ATTACHMENT_MAX_FILE_SIZE_BYTES),
      { message: `Each file must be ${ATTACHMENT_MAX_FILE_SIZE_LABEL} or less.` },
    )
    .optional(),
});
type FileUploadFormType = z.infer<typeof fileUploadFormSchema>;

const rfiExportFormSchema = z.object({
  to_name: z.string().optional(),
  spec_ref: z.string().optional(),
  date: z.string().optional(),
  date_required: z.string().optional(),
  description: z.string().optional(),

  contract_drawing_ref: z.string().optional(),
  shop_drawing_ref: z.string().optional(),
  distribution_list: z.string().optional(),

  information_not_on_contract: z.boolean().optional(),
  interpretation_of_contract: z.boolean().optional(),
  conflict_in_contract: z.boolean().optional(),
  coordination_problem: z.boolean().optional(),
});

type RfiExportFormType = z.infer<typeof rfiExportFormSchema>;
